import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseHandle } from '@il-colpo/shared/account';
import { useAccount } from '../../account/accountStore.ts';
import { useFlag } from '../../meta/liveStore.ts';
import { useSocialStore } from '../../social/socialStore.ts';
import { REPORT_REASONS, type ReportReason } from '../../social/types.ts';
import { useToastStore } from '../../state/toastStore.ts';
import { Button, IconButton, Modal } from '../../ui/index.ts';
import styles from './Social.module.css';

/**
 * What a player can do about another one at the table: ask to be friends, block, report. It is
 * offered for a player who has an account (a name with its four digits; a bot has none) and only to a
 * player who has an account of their own that is not a guest's, with friends switched on.
 */
export function PlayerActions({ handle }: { handle: string }) {
  const { t } = useTranslation('social');
  const client = useAccount((s) => s.client);
  const guest = useAccount((s) => s.snapshot.user?.anonymous ?? true);
  const enabled = useFlag('friends');
  const push = useToastStore((s) => s.push);
  const [open, setOpen] = useState<'menu' | 'report' | null>(null);
  const [reason, setReason] = useState<ReportReason>('chat');
  const [detail, setDetail] = useState('');
  const [busy, setBusy] = useState(false);
  const who = parseHandle(handle);
  if (!who || !client || guest || !enabled) return null;
  const social = client.social();
  const close = () => {
    setOpen(null);
    setDetail('');
    setReason('chat');
  };

  const addFriend = async () => {
    setBusy(true);
    const r = await social.request(handle);
    setBusy(false);
    close();
    if (!r.ok) {
      push(t('actions.failed'), 'danger');
      return;
    }
    push(
      t(`add.outcome.${r.value}`, { name: handle }),
      r.value === 'requested' || r.value === 'accepted' ? 'success' : 'info',
    );
    void useSocialStore.getState().refreshOverview(true);
  };

  const block = async () => {
    setBusy(true);
    const r = await social.block(handle);
    setBusy(false);
    close();
    if (!r.ok) {
      push(t('actions.failed'), 'danger');
      return;
    }
    push(t('actions.blocked', { name: handle }), 'success');
    void useSocialStore.getState().refreshOverview(true);
  };

  const report = async () => {
    setBusy(true);
    const r = await social.report(handle, reason, detail);
    setBusy(false);
    close();
    if (!r.ok) {
      push(t('report.failed'), 'danger');
      return;
    }
    push(
      t(`report.${r.value === 'reported' ? 'sent' : r.value}`),
      r.value === 'reported' ? 'success' : 'info',
    );
  };

  return (
    <>
      <IconButton
        icon="shield"
        label={t('actions.open', { name: handle })}
        onClick={() => {
          setOpen('menu');
        }}
      />
      <Modal
        open={open === 'menu'}
        onClose={close}
        title={handle}
        actions={
          <Button variant="ghost" onClick={close}>
            {t('actions.close')}
          </Button>
        }
      >
        <div className={styles['stack']}>
          <Button
            variant="secondary"
            loading={busy}
            onClick={() => {
              void addFriend();
            }}
          >
            {t('actions.addFriend')}
          </Button>
          <Button
            variant="secondary"
            loading={busy}
            onClick={() => {
              void block();
            }}
          >
            {t('actions.block')}
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              setOpen('report');
            }}
          >
            {t('actions.report')}
          </Button>
        </div>
      </Modal>
      <Modal
        open={open === 'report'}
        onClose={close}
        title={t('report.title', { name: handle })}
        actions={
          <>
            <Button variant="ghost" onClick={close}>
              {t('actions.close')}
            </Button>
            <Button
              loading={busy}
              onClick={() => {
                void report();
              }}
            >
              {t('report.send')}
            </Button>
          </>
        }
      >
        <div className={styles['stack']}>
          <fieldset className={styles['reasons']}>
            <legend>{t('report.reason')}</legend>
            <div className={styles['reasonGrid']}>
              {REPORT_REASONS.map((r) => (
                <label key={r} className={styles['reason']}>
                  <input
                    type="radio"
                    name="report-reason"
                    value={r}
                    checked={reason === r}
                    onChange={() => {
                      setReason(r);
                    }}
                  />
                  {t(`report.reasons.${r}`)}
                </label>
              ))}
            </div>
          </fieldset>
          <label className={styles['detailLabel']}>
            {t('report.detail')}
            <textarea
              className={styles['detail']}
              value={detail}
              maxLength={300}
              onChange={(e) => {
                setDetail(e.target.value);
              }}
            />
          </label>
        </div>
      </Modal>
    </>
  );
}
